import type { Expression, Map as MapboxMap } from 'mapbox-gl';
import type { TileLayerConfig } from '../types';

/** Prefix of every Map Query dataset layer id. */
export const DATASET_LAYER_PREFIX = 'dq-';

/** A filter no feature matches: the layer stays mounted but paints nothing. */
export const MATCH_NONE: Expression = ['==', ['literal', 0], ['literal', 1]];

/**
 * The style's layer ids, or null while the style itself is (re)loading, when
 * getStyle() throws. (Not isStyleLoaded(): that is also false while tiles load.)
 * @param {MapboxMap} map The Mapbox map.
 * @return {string[] | null} Layer ids, bottom first.
 */
export function styleLayerIds(map: MapboxMap): string[] | null {
  try {
    return (map.getStyle()?.layers ?? []).map((layer) => layer.id);
  } catch {
    return null;
  }
}

/**
 * Id of the layer DynamicTileLayer renders for one of a dataset's tileLayers, so
 * hover / hit-test code rebuilds the same id without duplicating the
 * `dq-${geoAttribute}-${idx}-${layerType}` string.
 * @param {TileLayerConfig} layer The tile layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The layer id.
 */
export const docLayerId = (layer: TileLayerConfig, idx: number): string =>
  `dq-${layer.geoAttribute ?? 'layer'}-${idx}-${layer.layerType}`;

/**
 * Id of the outline a fill layer with `outline` draws from its own source.
 * @param {TileLayerConfig} layer The fill layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The outline layer id.
 */
export const outlineLayerId = (layer: TileLayerConfig, idx: number): string =>
  `${DATASET_LAYER_PREFIX}${layer.geoAttribute ?? 'layer'}-${idx}-outline`;

/**
 * Id of the halo a `highlightInOwnColor` fill layer draws around its selected
 * feature, from the same source as its outline.
 * @param {TileLayerConfig} layer The fill layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The halo layer id.
 */
export const haloLayerId = (layer: TileLayerConfig, idx: number): string =>
  `${outlineLayerId(layer, idx)}-halo`;

/** Ends every names layer id, so a hit-test can tell the name labels apart. */
export const NAMES_LAYER_SUFFIX = '-names';

/**
 * Id of the name labels a tile layer with `labelFields` draws from its own source.
 * @param {TileLayerConfig} layer The labelled tile layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The names layer id.
 */
export const namesLayerId = (layer: TileLayerConfig, idx: number): string =>
  `${DATASET_LAYER_PREFIX}${layer.geoAttribute ?? 'layer'}-${idx}${NAMES_LAYER_SUFFIX}`;

/**
 * Id of the invisible aggs hit-target rendered for a `clusterCount` layer; the
 * hover popup queries it to read `_count` (documents stacked on the dot).
 * @param {TileLayerConfig} layer The clustered layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The hit-target layer id.
 */
export const clusterCountLayerId = (
  layer: TileLayerConfig,
  idx: number
): string => `dq-${layer.geoAttribute ?? 'layer'}-${idx}-count`;

export const buildTextFieldExpr = (fields: string[]): Expression => {
  if (fields.length === 1) return ['get', fields[0]] as Expression;
  const parts: unknown[] = [];
  fields.forEach((f, i) => {
    if (i > 0) parts.push(' ');
    parts.push(['get', f]);
  });
  return ['concat', ...parts] as Expression;
};

/** The tile layers drawn from one tile URL, with their index in the config. */
export interface TileSource {
  tiles: string[];
  layers: { layer: TileLayerConfig; idx: number }[];
}

/**
 * One source per tile URL: layers drawn from the same tiles (a dataset's circle and
 * symbol layers) share it, so each tile is fetched once. A layer with no tiles (a
 * fillOnlyHighlighted layer with nothing selected) gets no source.
 * @param {TileLayerConfig[]} tileLayers The dataset's tile layers.
 * @param {(string[] | null)[]} tilesByLayer Each tile layer's tiles, index for index.
 * @return {TileSource[]} The sources, in the order their first layer comes.
 */
export const groupByTileUrl = (
  tileLayers: TileLayerConfig[],
  tilesByLayer: (string[] | null)[]
): TileSource[] => {
  const sources = new Map<string, TileSource>();
  tileLayers.forEach((layer, idx) => {
    const tiles = tilesByLayer[idx];
    if (!tiles) return;
    const source = sources.get(tiles[0]);
    if (source) source.layers.push({ layer, idx });
    else sources.set(tiles[0], { tiles, layers: [{ layer, idx }] });
  });
  return [...sources.values()];
};

/**
 * The layer each tile layer is added below, by its index, to keep the config's draw
 * order: a source adds all its layers at once, so a layer whose source comes after a
 * later layer's would otherwise draw above it.
 * @param {TileSource[]} sources The sources, in the order they mount.
 * @param {TileLayerConfig[]} tileLayers The dataset's tile layers.
 * @return {Map<number, string>} The id to add each tile layer below, where it needs one.
 */
export const belowLayerIds = (
  sources: TileSource[],
  tileLayers: TileLayerConfig[]
): Map<number, string> => {
  const added = new Set<number>();
  const below = new Map<number, string>();
  sources.forEach(({ layers }) =>
    layers.forEach(({ idx }) => {
      const next = tileLayers.findIndex((_, j) => j > idx && added.has(j));
      if (next !== -1) below.set(idx, docLayerId(tileLayers[next], next));
      added.add(idx);
    })
  );
  return below;
};
